import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { useAuthorization } from "~/contexts/authorizationProvider";
import { useFeatureFlags } from "~/contexts/featureFlagProvider";
import { useOrg } from "~/contexts/orgProvider";
import { useUser } from "~/contexts/userProvider";
import { featureFlagAiEnabled } from "~/shared/constants/feature-flag.constants";

import { createAssistantConversationLifecycle } from "./assistant-conversation-lifecycle";
import { assistantConversationScopeKey } from "./assistant-conversation-scope";
import {
  type AssistantConversationActions,
  type AssistantConversationState,
  type AssistantConversationStore,
  createAssistantConversationStore,
} from "./assistant-conversation-store";

type AssistantConversationContextValue = AssistantConversationState & AssistantConversationActions;

const AssistantConversationContext = createContext<AssistantConversationContextValue | null>(null);

interface ScopedStore {
  key: string | null;
  store: AssistantConversationStore | null;
}

const StoreSubscription = ({ store, children }: PropsWithChildren<{ store: AssistantConversationStore }>) => {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const value = useMemo<AssistantConversationContextValue>(
    () => ({
      ...state,
      submitQuery: store.submitQuery,
      retryRecovery: store.retryRecovery,
      stop: store.stop,
      clear: store.clear,
      clearDebug: store.clearDebug,
      setPanelArtifactKey: store.setPanelArtifactKey,
      setPanelActionProposalId: store.setPanelActionProposalId,
      stageActionSelection: store.stageActionSelection,
      setPanelOpened: store.setPanelOpened,
      closePanel: store.closePanel,
      showLatestArtifact: store.showLatestArtifact,
      submitFeedback: store.submitFeedback,
      setDraft: store.setDraft,
      setActionDraftContext: store.setActionDraftContext,
      addAttachments: store.addAttachments,
      removeAttachment: store.removeAttachment,
      retryAttachment: store.retryAttachment,
    }),
    [state, store],
  );

  return <AssistantConversationContext.Provider value={value}>{children}</AssistantConversationContext.Provider>;
};

export const AssistantConversationProvider = ({ children }: PropsWithChildren) => {
  const user = useUser();
  const org = useOrg();
  const { getFeatureFlag } = useFeatureFlags();
  const authorization = useAuthorization();
  const enabled = getFeatureFlag(featureFlagAiEnabled, false);
  const [storeLifecycle] = useState(createAssistantConversationLifecycle);
  const incomingScopeKey = assistantConversationScopeKey({
    enabled,
    userClerkId: user.userClerkId,
    userId: user.userId,
    orgClerkId: org.orgClerkId,
    orgId: org.orgId,
    isSuperAdmin: authorization.isSuperAdmin,
    hasAllPermissions: authorization.hasAllPermissions,
    role: authorization.role,
    permissions: authorization.permissions,
  });
  const [scopedStore, setScopedStore] = useState<ScopedStore>(() => ({
    key: incomingScopeKey,
    store: incomingScopeKey
      ? createAssistantConversationStore({
          orgId: org.orgId,
          userId: user.userId,
          isSuperAdmin: authorization.isSuperAdmin,
          selfName: `${user.userFirstName} ${user.userLastName}`.trim(),
        })
      : null,
  }));

  useEffect(() => {
    if (scopedStore.key === incomingScopeKey) {
      return;
    }
    scopedStore.store?.dispose();
    setScopedStore({
      key: incomingScopeKey,
      store: incomingScopeKey
        ? createAssistantConversationStore({
            orgId: org.orgId,
            userId: user.userId,
            isSuperAdmin: authorization.isSuperAdmin,
            selfName: `${user.userFirstName} ${user.userLastName}`.trim(),
          })
        : null,
    });
  }, [authorization.isSuperAdmin, incomingScopeKey, org.orgId, scopedStore, user.userFirstName, user.userId, user.userLastName]);

  useEffect(() => {
    storeLifecycle.retain();
    return () => storeLifecycle.release(scopedStore.store);
  }, [scopedStore.store, storeLifecycle]);

  if (scopedStore.key !== incomingScopeKey || !scopedStore.store) {
    return <AssistantConversationContext.Provider value={null}>{children}</AssistantConversationContext.Provider>;
  }

  return <StoreSubscription store={scopedStore.store}>{children}</StoreSubscription>;
};

export const useAssistantConversation = (): AssistantConversationContextValue | null => useContext(AssistantConversationContext);
